<template>
  <div data-cy="cf-submission-view" class="space-y-6 pb-10">
    <div>
      <button
        type="button"
        class="mb-2 text-sm text-blue-600 hover:underline"
        data-cy="cf-back-to-list"
        @click="emit('back')"
      >
        <i class="fas fa-chevron-left mr-1"></i>Back to Forms &amp; Checklists
      </button>

      <div class="flex flex-wrap items-start justify-between gap-x-8 gap-y-4">
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-2">
            <h3 class="mb-0 text-xl font-semibold text-gray-900">
              {{ submission.name }}
            </h3>
            <CustomFormSubmissionStatusChip :status="submission.status" />
            <CustomFormBadge v-if="isArchived" data-cy="cf-archived-badge">
              Archived
            </CustomFormBadge>
            <!-- An archived form gates nothing, so the badge claiming it does
                 would be a lie. -->
            <CustomFormBadge
              v-else-if="submission.required_for_close"
              tone="warn"
            >
              Required to close
            </CustomFormBadge>
          </div>

          <p v-if="submission.description" class="mt-1 text-sm text-gray-500">
            {{ submission.description }}
          </p>

          <CustomFormProgressBar
            class="mt-3 max-w-md"
            :answered="submission.progress.required_answered"
            :total="submission.progress.required_total"
            :complete="submission.progress.complete"
            data-cy="cf-required-progress-bar"
            label-data-cy="cf-required-progress"
            :suffix="progressSuffix"
            suffix-data-cy="cf-total-progress"
          />
        </div>

        <div v-if="sections.length" class="flex flex-col items-end gap-1">
          <!-- Wraps: search plus Jump to plus Edit answers plus Archive is far
               wider than a phone, and without this the strip runs off the right
               edge of the page rather than stacking. -->
          <div class="flex flex-wrap items-center justify-end gap-3">
            <!-- Full width on a phone so it owns its row and the two controls wrap
                 together beneath it; at 300px fixed it left whichever control came
                 last stranded alone on the next line. -->
            <CombinedSearch
              v-model="query"
              label=""
              placeholder="Search questions and answers"
              input-id="cf-search-input"
              :show-clear-button="true"
              class="w-full sm:w-[300px]"
              data-cy="cf-search"
            />
            <CustomFormJumpMenu
              v-if="sectionRows.length > 1"
              :sections="outlineSections"
              align="right"
              @jump-section="jump(jumpToSection, $event)"
              @jump-question="jump(jumpToQuestion, $event)"
            />
            <!-- Reading is the default and the common case: an office user opens
                 a form to see what came back, not to type into it. Inputs are
                 opt-in so the scannable render is not traded away for an edit
                 affordance nobody asked for on arrival.

                 Withheld entirely on an archived form: the server refuses every
                 answer write once the form is off the job, so offering the toggle
                 would walk the user into a 422 with no way to tell why. Restore it
                 first — the same reasoning as Submit below. -->
            <button
              v-if="mutable && !isArchived"
              type="button"
              :class="[
                'whitespace-nowrap rounded-md border px-3 py-2 text-sm',
                editing
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              ]"
              :aria-pressed="editing"
              data-cy="cf-toggle-edit"
              @click="emit('update:editing', !editing)"
            >
              <i class="fas fa-pen mr-1.5 text-xs"></i>
              {{ editing ? "Done editing" : "Edit answers" }}
            </button>
            <!-- Only once every required answer is in. Submitting is what clears
                 the close-out gate, so offering it earlier would turn this into
                 the override that V1 deliberately does not have. -->
            <button
              v-if="canSubmit"
              type="button"
              class="whitespace-nowrap rounded-md border border-green-700 bg-green-700 px-3 py-2 text-sm text-white hover:bg-green-800 disabled:opacity-60"
              :disabled="submitting"
              data-cy="cf-submit"
              @click="emit('submit')"
            >
              <i class="fas fa-check mr-1.5 text-xs"></i>
              {{ submitting ? "Submitting…" : "Submit checklist" }}
            </button>
            <!-- Withheld on anything the server would refuse: an unsubmitted or
                 archived form cannot be emailed at all. Read-only surfaces get
                 nothing either, since they have no job-scoped handler to emit
                 into. -->
            <button
              v-if="canSend"
              type="button"
              class="whitespace-nowrap rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              data-cy="cf-send"
              @click="emit('send')"
            >
              <i class="fa-regular fa-paper-plane mr-1.5 text-xs"></i>Send to
              customer
            </button>
            <!-- A labelled button here, an icon on the list row: this strip is
                 already labelled controls, so a bare glyph would be the odd one
                 out, while the compact row has no space for a word. Offered on
                 both because realising a form does not belong on the job usually
                 happens while reading it. -->
            <button
              v-if="mutable && isArchived"
              type="button"
              class="whitespace-nowrap rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              data-cy="cf-unarchive"
              @click="emit('unarchive')"
            >
              <i class="fa-regular fa-box-open mr-1.5 text-xs"></i>Restore
            </button>
            <button
              v-else-if="mutable"
              type="button"
              class="whitespace-nowrap rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              data-cy="cf-archive"
              @click="emit('archive')"
            >
              <i class="fa-regular fa-archive mr-1.5 text-xs"></i>Archive
            </button>
          </div>
          <span
            v-if="active"
            class="text-xs text-gray-500"
            data-cy="cf-search-count"
          >
            {{ matchCount }} {{ matchCount === 1 ? "question" : "questions" }}
          </span>
        </div>
      </div>
    </div>

    <!-- Says what archiving did and what is still true: the answers are intact,
         which is the reason to archive rather than delete. -->
    <div
      v-if="isArchived"
      class="flex items-start gap-2 rounded-lg bg-gray-100 p-4"
      data-cy="cf-archived-banner"
    >
      <i class="fas fa-box-archive mt-0.5 text-gray-600"></i>
      <p class="text-sm font-medium text-gray-700">
        This form is archived. Its answers are kept, and it no longer holds its
        visit open. Restore it from the All filter on the forms list.
      </p>
    </div>

    <!-- Deliberately does not claim the form is now read-only: an admin may
         amend a submitted form, and every write is audited. -->
    <div
      v-else-if="submission.status === 'submitted'"
      class="flex items-start gap-2 rounded-lg bg-green-50 p-4"
      data-cy="cf-submitted-banner"
    >
      <i class="fas fa-check mt-0.5 text-green-700"></i>
      <p class="text-sm font-medium text-green-700">
        {{ submittedLine }}
      </p>
    </div>

    <!-- The same text the customer's PDF opens with, so the office reads what
         the customer will read before it goes out. -->
    <div
      v-if="submission.summary"
      class="rounded-lg border border-gray-200 bg-gray-50 p-4"
      data-cy="cf-summary"
    >
      <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Summary
      </h4>
      <p class="mt-1 whitespace-pre-line text-sm leading-relaxed text-gray-900">
        {{ submission.summary }}
      </p>
    </div>

    <p
      v-if="!sections.length"
      class="text-sm text-gray-500"
      data-cy="cf-no-sections"
    >
      This form has no questions.
    </p>

    <p
      v-if="active && !matchCount"
      class="text-sm text-gray-500"
      data-cy="cf-no-matches"
    >
      No question or answer matches &ldquo;{{ query }}&rdquo;.
      <button
        type="button"
        class="text-blue-600 hover:underline"
        data-cy="cf-clear-search"
        @click="clear"
      >
        Clear the search
      </button>
      to see the whole form.
    </p>

    <div>
      <div
        v-for="row in visibleSections"
        :key="row.section.id"
        class="cf-section"
        :data-cf-section-key="row.section.id"
        :data-cy="`cf-section-${row.section.id}`"
      >
        <div class="flex flex-wrap items-baseline gap-3">
          <h4
            class="text-xs font-semibold uppercase tracking-wide text-gray-500"
          >
            {{ row.section.name || "Untitled section" }}
          </h4>
          <!-- Hidden while searching, where the questions on screen are a
               subset and the tally would not match them. -->
          <CustomFormBadge
            v-if="!active && row.unmet"
            tone="warn"
            :data-cy="`cf-needs-attention-${row.section.id}`"
          >
            {{ row.unmet }} need attention
          </CustomFormBadge>
          <span
            v-else-if="!active && row.required"
            class="text-xs text-gray-500"
            :data-cy="`cf-all-answered-${row.section.id}`"
          >
            All {{ row.required }} required answered
          </span>
        </div>
        <p v-if="row.section.description" class="mt-1 text-xs text-gray-500">
          {{ row.section.description }}
        </p>
        <div class="mt-4 space-y-5 pl-6">
          <p v-if="!row.questions.length" class="text-sm text-gray-500">
            No questions.
          </p>
          <CustomFormAnswerRow
            v-for="question in row.questions"
            :key="question.id"
            :data-cf-question-key="question.id"
            :question="question"
            :answer="question.answer"
            :show-author="!soleAuthor"
            :editing="editable && !!$slots.answer"
          >
            <!-- Gated on the `editing` prop rather than on the parent supplying
                 the slot conditionally: slot presence is not reactive, so a
                 parent toggling its own v-if would not re-render the row. The
                 parent always supplies the slot; this decides when to use it. -->
            <template v-if="editable && $slots.answer" #answer="slotProps">
              <slot name="answer" v-bind="slotProps" />
            </template>
          </CustomFormAnswerRow>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import moment from "moment-timezone";
import { computed, nextTick, watch } from "vue";

import CombinedSearch from "@/components/Seranova/SearchInput/CombinedSearch.vue";
import CONSTANTS from "@/Constants.js";
import type { CustomFormSubmission } from "@/typings/customForm";
import CustomFormAnswerRow from "./CustomFormAnswerRow.vue";
import CustomFormBadge from "./CustomFormBadge.vue";
import CustomFormJumpMenu from "./CustomFormJumpMenu.vue";
import CustomFormProgressBar from "./CustomFormProgressBar.vue";
import { type QuestionState, questionState } from "./customFormQuestionState";
import CustomFormSubmissionStatusChip from "./CustomFormSubmissionStatusChip.vue";
import { useFormSearch } from "./useFormSearch";
import { useOutlineScroll } from "./useOutlineScroll";

const props = withDefaults(
  defineProps<{
    submission: CustomFormSubmission;
    // Whether the user has turned editing on. No submission state forbids it —
    // the view only renders for an open submission, and the amend gate no longer
    // closes at submit.
    editing?: boolean;
    // A surface that reviews forms rather than filling them in: the customer
    // record's Forms tab aggregates across jobs, and answers are written from the
    // job that owns the save layer. Withholds Edit and Submit, which would
    // otherwise emit into handlers such a parent does not have.
    readOnly?: boolean;
    // A question to scroll to and pulse on arrival, for a deep link that names
    // one. Null means land at the top, which is the normal case.
    focusQuestionId?: number | null;
    submitting?: boolean;
  }>(),
  { editing: false, readOnly: false, focusQuestionId: null, submitting: false }
);

const emit = defineEmits<{
  archive: [];
  unarchive: [];
  back: [];
  send: [];
  submit: [];
  "update:editing": [value: boolean];
}>();

const isArchived = computed(() => Boolean(props.submission.archived_at));

// Whether this surface writes at all. Folded into editable and canSubmit below so
// each control has one gate rather than a growing conjunction per button.
const mutable = computed(() => !props.readOnly);

// Archiving wins over an `editing` already in flight. The toggle is withheld on an
// archived form, but the prop can still arrive true — a background refresh can land
// archived_at while the form is open in edit mode — and inputs whose writes the
// server refuses are worse than no inputs.
const editable = computed(
  () => mutable.value && props.editing && !isArchived.value
);

// The two conditions the send endpoint refuses on that this view can see. The
// third, a form with nothing customer-visible in it, is not derivable from the
// payload, so that one is left to the server and surfaced as the modal's error.
// See CustomFormSubmissionList for why an unsubmitted form is withheld rather
// than prompted for, and which reading of "incomplete" that answers.
const canSend = computed(
  () =>
    mutable.value &&
    !isArchived.value &&
    props.submission.status === "submitted"
);

// The server refuses an incomplete submit too (SubmitSubmission, TC-505), so
// this is no longer the only thing keeping finalizing from doubling as a gate
// override — it is here to withhold a button that would always fail. Archived
// sits in the same position: the server refuses that too, so the button should
// not be offered.
const canSubmit = computed(
  () =>
    mutable.value &&
    !isArchived.value &&
    props.submission.status === "in_progress" &&
    props.submission.progress.complete
);

// The contract already orders these, but position is the authority.
const sections = computed(() =>
  [...(props.submission.sections ?? [])].sort((a, b) => a.position - b.position)
);

// Sorted questions and their tallies, computed once per section rather than
// per binding in the template.
const sectionRows = computed(() =>
  sections.value.map((section) => {
    const questions = [...section.questions].sort(
      (a, b) => a.position - b.position
    );
    return {
      section,
      questions,
      required: questions.filter((question) => question.required).length,
      unmet: questions.filter(
        (question) => questionState(question, question.answer) === "unmet"
      ).length
    };
  })
);

const {
  query,
  active,
  filtered: visibleSections,
  matchCount,
  clear
} = useFormSearch(sectionRows);

// Built from the unfiltered tree: the outline is how you get back to a question
// the search hid, so it has to keep listing every one.
const outlineSections = computed(() =>
  sectionRows.value.map((row) => {
    const questions = row.questions.map((question) => ({
      key: question.id,
      title: question.title,
      state: questionState(question, question.answer)
    }));
    return {
      key: row.section.id,
      name: row.section.name,
      state: sectionState(questions),
      questions
    };
  })
);

// Credited once here when one person answered everything; the moment a second
// contributes, the rows carry it per-answer instead.
const soleAuthor = computed(() => {
  const names = new Set(
    sections.value
      .flatMap((section) => section.questions)
      .map((question) => question.answer?.created_by_name)
      .filter(Boolean)
  );
  return names.size === 1 ? [...names][0] : null;
});

const progressSuffix = computed(() => {
  const { answered, total } = props.submission.progress;
  const parts = [`${answered} / ${total} answered overall`];
  if (soleAuthor.value) parts.push(`Answered by ${soleAuthor.value}`);
  return parts.join(" · ");
});

// A section is only satisfied once nothing in it is outstanding.
function sectionState(questions: { state: QuestionState }[]): QuestionState {
  if (questions.some((question) => question.state === "unmet")) return "unmet";
  if (questions.length && questions.every((q) => q.state === "satisfied")) {
    return "satisfied";
  }
  return "optional";
}

const { jumpToSection, jumpToQuestion } = useOutlineScroll();

async function jump(
  scrollTo: (key: string | number) => void,
  key: string | number
) {
  // The target may be filtered off the page, so put the whole form back and let
  // it render before looking for the anchor.
  if (active.value) {
    clear();
    await nextTick();
  }
  scrollTo(key);
}

// Immediate so a deep link is honoured on arrival, not only on a later change.
// The nextTick is what makes it work at all on arrival: the anchor this targets
// does not exist until the sections have rendered.
watch(
  () => props.focusQuestionId,
  async (id) => {
    if (!id) return;
    await nextTick();
    jump(jumpToQuestion, id);
  },
  { immediate: true }
);

// Falls back to the bare fact rather than a dangling "Submitted by".
const submittedLine = computed(() => {
  const who = props.submission.submitted_by_name;
  const when = props.submission.submitted_at
    ? moment(props.submission.submitted_at).format(CONSTANTS.DATE_FORMAT)
    : null;
  const subject = who ? `Submitted by ${who}` : "Submitted";
  return when ? `${subject} on ${when}` : subject;
});
</script>

<style scoped>
/* Reaches CustomFormAnswerRow too: a child's root element carries this
   component's scope id. */
.cf-jump-pulse {
  border-radius: 0.5rem;
  animation: cf-jump-pulse 1200ms ease;
}
@keyframes cf-jump-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5);
  }
  100% {
    box-shadow: 0 0 0 12px rgba(59, 130, 246, 0);
  }
}
@media (prefers-reduced-motion: reduce) {
  .cf-jump-pulse {
    animation: none;
  }
}

/* The first section needs no rule — it has the header above it. */
.cf-section + .cf-section {
  margin-top: 1.5rem;
  border-top: 1px solid rgb(229 231 235);
  padding-top: 1.5rem;
}
</style>
